import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { useStringsContext } from '../../../../contexts/strings';
import MultipleSelectWithValidate from '../../../common/input/multiple-select-with-validate';
import { useSquad } from '../../squad-database/hooks/squad';
import { SelectOptions } from '../../../common/input/select-with-validate';
import svStrings from '../../../../sv-strings';
import { QuizQuestion } from '../types';
import { Option } from '../../../../types';
import { InputWithValidateProps } from '../../../common/input/text-input-with-validate';

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;

const playerMultipleSelection = yup
  .array()
  .nullable()
  .required(svStrings.validations.mustSelectAlternative);

type QuestionPlayerAlternativesKey = keyof Pick<QuizQuestion, 'playerAlternatives'>;

const playerAlternativesKey: QuestionPlayerAlternativesKey = 'playerAlternatives';

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  selectedAbbrv: string;
  checkboxes?: boolean;
  onSelectedPlayers: (selectedPlayers: SelectOptions | null) => void;
  initialSelectedPlayers?: SelectOptions;
}

const PollSelectedPlayersClub = ({
  selectedAbbrv,
  initialSelectedPlayers,
  onValidation,
  hideValidationError,
  checkboxes,
  onSelectedPlayers,
}: Props) => {
  const { strings } = useStringsContext();
  const { fetchSquad } = useSquad();
  const initSelectedPlayers = useRef(false);

  const [squadLoading, setSquadLoading] = useState(false);

  const [playersOptions, setPlayersOptions] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  const [selectedPlayers, setSelectedPlayers] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  useEffect(() => {
    if (initialSelectedPlayers && !initSelectedPlayers.current) {
      setSelectedPlayers(initialSelectedPlayers);
      onSelectedPlayers(initialSelectedPlayers);
      initSelectedPlayers.current = true;
    }
  }, [initialSelectedPlayers, onSelectedPlayers]);

  useEffect(() => {
    const getSquad = async () => {
      if (!playersOptions.options.length && selectedAbbrv) {
        setSquadLoading(true);
        try {
          const playerSquad = await fetchSquad(
            selectedAbbrv,
            Number(REACT_APP_DEFAULT_LEAGUE_SEASON),
            false,
            false
          );

          if (playerSquad) {
            setPlayersOptions({
              options: playerSquad.map((player) => String(player.fogisId)),
              optionsText: playerSquad.map(
                (player) => player.displayName || String(player.fogisId)
              ),
            });
          }
        } catch (error) {
          // Do nothing
        }
        setSquadLoading(false);
      }
    };
    getSquad();
  }, [fetchSquad, selectedAbbrv, playersOptions.options]);

  const handlePlayersSelectedValue = useCallback(
    (key: QuestionPlayerAlternativesKey, value: Option[]) => {
      const optionsText = value?.map((option) => {
        const index = playersOptions.options.findIndex((o) => o === option);
        return playersOptions.optionsText[index];
      });
      const updatedSelectedPlayers = {
        options: value,
        optionsText: optionsText,
      };
      setSelectedPlayers(updatedSelectedPlayers);
      onSelectedPlayers(updatedSelectedPlayers);
    },
    [onSelectedPlayers, playersOptions.options, playersOptions.optionsText]
  );

  return (
    <MultipleSelectWithValidate
      name={playerAlternativesKey}
      options={playersOptions.options}
      optionsText={playersOptions.optionsText}
      validation={playerMultipleSelection}
      label={strings.polls.quizSelectPlayerLabel}
      onValidation={onValidation}
      value={selectedPlayers.options || []}
      onValue={(key, value) => handlePlayersSelectedValue(playerAlternativesKey, value)}
      hideValidationError={hideValidationError}
      disabled={squadLoading}
      checkboxes={checkboxes}
    />
  );
};

export default PollSelectedPlayersClub;
